import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Typography,
  Paper,
  LinearProgress,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
  Fade,
  Chip,
  Container,
  Card,
  CardContent
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Check as CheckIcon,
  Save as SaveIcon,
  AutoMode as AutoIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { ApiService } from '../../services/api';

// Import existing step components
import { CompanyInformationStep } from './steps/CompanyInformationStep';
import { ProjectRequirementsStep } from './steps/ProjectRequirementsStep';
import { TechnicalPreferencesStep } from './steps/TechnicalPreferencesStep';
import { TimelineBudgetStep } from './steps/TimelineBudgetStep';
import { ReviewSubmitStep } from './steps/ReviewSubmitStep';
import { UserRegistrationStep } from './steps/UserRegistrationStep';

// Performance tracking utility
const trackPerformance = (action: string, duration: number) => {
  console.log(`Performance: ${action} took ${duration}ms`);
};

// Accessibility announcements
const announceToScreenReader = (message: string) => {
  const liveRegion = document.getElementById('aria-live-region');
  if (liveRegion) {
    liveRegion.textContent = message;
    setTimeout(() => {
      liveRegion.textContent = '';
    }, 1000);
  }
};

// Data interfaces
interface UnifiedOnboardingData {
  user_registration?: {
    user_name?: string;
    user_email?: string;
    user_password?: string;
    user_password_confirmation?: string;
  };
  company_info?: {
    company_name?: string;
    website?: string;
    industry?: string;
    company_size?: string;
    phone?: string;
    country?: string;
    address?: string;
    description?: string;
  };
  project_overview?: {
    project_type?: string;
    project_description?: string;
    features?: string[];
    platforms?: string[];
    expected_users?: string;
    performance_priority?: string;
    integration_needs?: string[];
  };
  technical_requirements?: {
    preferred_technologies?: string[];
    hosting_preference?: string;
    cms_preference?: string;
    maintenance_level?: string;
    performance_requirements?: string[];
    security_requirements?: string[];
    backup_requirements?: string;
  };
  timeline_budget?: {
    budget_range?: string;
    desired_timeline?: string;
    launch_date?: string;
    timeline_flexibility?: string;
    priority_factors?: string[];
    payment_preference?: string;
    rush_requirements?: string;
    additional_notes?: string;
  };
}

interface ValidationErrors {
  [stepKey: string]: {
    [fieldKey: string]: string;
  };
}

// Step configuration
const STEPS = [
  { key: 'user_registration', label: 'Account Creation', component: UserRegistrationStep },
  { key: 'company_info', label: 'Company Information', component: CompanyInformationStep },
  { key: 'project_overview', label: 'Project Requirements', component: ProjectRequirementsStep },
  { key: 'technical_requirements', label: 'Technical Preferences', component: TechnicalPreferencesStep },
  { key: 'timeline_budget', label: 'Timeline & Budget', component: TimelineBudgetStep },
  { key: 'review_submit', label: 'Review & Submit', component: ReviewSubmitStep },
];

interface UnifiedOnboardingWizardProps {
  onComplete?: () => void;
  onError?: (error: string) => void;
}

/**
 * Unified Onboarding Wizard Component
 * Combines user registration, company information, and project creation in one flow
 * Eliminates duplicate questions and creates everything at once
 */
const UnifiedOnboardingWizard: React.FC<UnifiedOnboardingWizardProps> = ({
  onComplete,
  onError
}) => {
  // State management
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [onboardingData, setOnboardingData] = useState<UnifiedOnboardingData>({});
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [stepValidationStates, setStepValidationStates] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState<boolean>(true);
  
  // Auto-save timer
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  const { loginWithToken, getDashboardPath } = useAuth();

  /**
   * Handle step data changes
   */
  const handleStepDataChange = useCallback((stepKey: string, stepData: any) => {
    console.log('Step data change:', stepKey, stepData);
    
    setOnboardingData(prev => ({
      ...prev,
      [stepKey]: {
        ...prev[stepKey as keyof UnifiedOnboardingData],
        ...stepData
      }
    }));
    
    // Clear validation errors for this step
    setValidationErrors(prev => ({
      ...prev,
      [stepKey]: {}
    }));
  }, []);

  /**
   * Handle validation state changes
   */
  const handleValidationChange = useCallback((stepKey: string, isValid: boolean) => {
    setStepValidationStates(prev => {
      // Only update if the value has changed
      if (prev[stepKey] === isValid) {
        return prev;
      }
      
      return {
        ...prev,
        [stepKey]: isValid
      };
    });
  }, []);

  /**
   * Navigate to specific step
   */
  const goToStep = useCallback((stepIndex: number) => {
    if (stepIndex >= 0 && stepIndex < STEPS.length) {
      const stepName = STEPS[stepIndex].label;
      announceToScreenReader(`Navigated to ${stepName}`);
      setCurrentStep(stepIndex);
    }
  }, []);

  /**
   * Move to next step
   */
  const handleNext = useCallback(() => {
    if (currentStep < STEPS.length - 1) {
      const nextStep = currentStep + 1;
      const nextStepName = STEPS[nextStep].label;
      announceToScreenReader(`Moving to ${nextStepName}`);
      setCurrentStep(nextStep);
    }
  }, [currentStep]);

  /**
   * Move to previous step
   */
  const handleBack = useCallback(() => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      const prevStepName = STEPS[prevStep].label;
      announceToScreenReader(`Returning to ${prevStepName}`);
      setCurrentStep(prevStep);
    }
  }, [currentStep]);

  /**
   * Handle final submission of unified onboarding
   */
  const handleSubmitOnboarding = useCallback(async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      // Transform data to match backend API expectations
      const transformedData = {
        // User registration fields (flat structure)
        user_name: onboardingData.user_registration?.user_name || '',
        user_email: onboardingData.user_registration?.user_email || '',
        user_password: onboardingData.user_registration?.user_password || '',
        user_password_confirmation: onboardingData.user_registration?.user_password_confirmation || onboardingData.user_registration?.user_password || '',
        
        // Company information (nested structure)
        company_info: {
          company_name: onboardingData.company_info?.company_name || '',
          website: onboardingData.company_info?.website || '',
          industry: onboardingData.company_info?.industry || '',
          company_size: onboardingData.company_info?.company_size || '',
          phone: onboardingData.company_info?.phone || '',
          country: onboardingData.company_info?.country || '',
          address: onboardingData.company_info?.address || '',
          description: onboardingData.company_info?.description || ''
        },
        
        // Project requirements (nested structure)
        project_overview: {
          project_type: onboardingData.project_overview?.project_type || '',
          project_description: onboardingData.project_overview?.project_description || '',
          features: onboardingData.project_overview?.features || [],
          platforms: onboardingData.project_overview?.platforms || [],
          expected_users: onboardingData.project_overview?.expected_users || '',
          performance_priority: onboardingData.project_overview?.performance_priority || '',
          integration_needs: onboardingData.project_overview?.integration_needs || []
        },
        
        // Technical requirements (nested structure)
        technical_requirements: {
          preferred_technologies: onboardingData.technical_requirements?.preferred_technologies || [],
          hosting_preference: onboardingData.technical_requirements?.hosting_preference || '',
          cms_preference: onboardingData.technical_requirements?.cms_preference || '',
          maintenance_level: onboardingData.technical_requirements?.maintenance_level || '',
          performance_requirements: onboardingData.technical_requirements?.performance_requirements || [],
          security_requirements: onboardingData.technical_requirements?.security_requirements || [],
          backup_requirements: onboardingData.technical_requirements?.backup_requirements || ''
        },
        
        // Timeline and budget (nested structure)
        timeline_budget: {
          desired_timeline: onboardingData.timeline_budget?.desired_timeline || '',
          budget_range: onboardingData.timeline_budget?.budget_range || '',
          launch_date: onboardingData.timeline_budget?.launch_date || '',
          timeline_flexibility: onboardingData.timeline_budget?.timeline_flexibility || '',
          priority_factors: onboardingData.timeline_budget?.priority_factors || [],
          payment_preference: onboardingData.timeline_budget?.payment_preference || '',
          rush_requirements: onboardingData.timeline_budget?.rush_requirements || '',
          additional_notes: onboardingData.timeline_budget?.additional_notes || ''
        }
      };
      
      console.log('Submitting transformed data:', transformedData);
      
      const response = await ApiService.post<{
        success: boolean;
        message: string;
        data: {
          user: any;
          company: any;
          project: any;
          token: string;
          token_type: string;
          expires_in: number;
          next_steps: string[];
        };
      }>('/onboarding/unified', transformedData);
      
      console.log('Onboarding submitted successfully:', response);
      
      if (response.success && response.data.token) {
        // Automatically log the user in using AuthContext
        await loginWithToken(response.data.token, response.data.user);
        
        // Handle successful submission
        setShowSuccessMessage(true);
        announceToScreenReader('Registration and onboarding completed successfully! Redirecting to your dashboard.');
        
        // Redirect to appropriate dashboard after success
        const dashboardPath = getDashboardPath();
        setTimeout(() => {
          window.location.href = dashboardPath;
        }, 2000);
        
        if (onComplete) {
          onComplete();
        }
      }
      
    } catch (error: any) {
      console.error('Error submitting onboarding:', error);
      const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred';
      setSubmitError(errorMessage);
      
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [onboardingData, onComplete, onError]);

  /**
   * Get progress percentage
   */
  const getProgressPercentage = useCallback((): number => {
    if (currentStep === STEPS.length - 1) {
      // On review step, check if all previous steps are completed
      const requiredSteps = STEPS.slice(0, -1);
      const completedSteps = requiredSteps.filter(step => 
        stepValidationStates[step.key] === true
      ).length;
      return Math.min((completedSteps / requiredSteps.length) * 90, 90);
    }
    
    return (currentStep / (STEPS.length - 1)) * 100;
  }, [currentStep, stepValidationStates]);

  /**
   * Check if user can proceed to next step
   */
  const canProceedToNext = useMemo((): boolean => {
    const currentStepConfig = STEPS[currentStep];
    const canProceed = stepValidationStates[currentStepConfig.key] === true;
    
    console.log('Can proceed check:', {
      currentStep,
      currentStepKey: currentStepConfig.key,
      stepValidationStates,
      canProceed
    });
    
    return canProceed;
  }, [currentStep, stepValidationStates]);

  /**
   * Render current step component
   */
  const renderCurrentStep = () => {
    const currentStepConfig = STEPS[currentStep];
    const stepKey = currentStepConfig.key;
    const stepErrors = validationErrors[stepKey] || {};
    
    // Convert string errors to string array format expected by components
    const formattedErrors: Record<string, string[]> = {};
    Object.entries(stepErrors).forEach(([field, error]) => {
      formattedErrors[field] = [error as string];
    });
    
    // Handle each step explicitly to avoid TypeScript issues
    switch (stepKey) {
      case 'user_registration':
        return (
          <UserRegistrationStep
            data={onboardingData.user_registration || {}}
            onChange={(data: any) => handleStepDataChange(stepKey, data)}
            onValidationChange={(isValid: boolean) => handleValidationChange(stepKey, isValid)}
            errors={formattedErrors}
          />
        );
        
      case 'company_info':
        return (
          <CompanyInformationStep
            data={onboardingData.company_info || {}}
            onChange={(data: any) => handleStepDataChange(stepKey, data)}
            onValidationChange={(isValid: boolean) => handleValidationChange(stepKey, isValid)}
            errors={formattedErrors}
          />
        );
        
      case 'project_overview':
        return (
          <ProjectRequirementsStep
            data={onboardingData.project_overview || {}}
            onChange={(data: any) => handleStepDataChange(stepKey, data)}
            onValidationChange={(isValid: boolean) => handleValidationChange(stepKey, isValid)}
            errors={formattedErrors}
          />
        );
        
      case 'technical_requirements':
        return (
          <TechnicalPreferencesStep
            data={onboardingData.technical_requirements as any || {}}
            onChange={(data: any) => handleStepDataChange(stepKey, data)}
            onValidationChange={(isValid: boolean) => handleValidationChange(stepKey, isValid)}
            errors={formattedErrors}
          />
        );
        
      case 'timeline_budget':
        return (
          <TimelineBudgetStep
            data={onboardingData.timeline_budget || {}}
            onChange={(data: any) => handleStepDataChange(stepKey, data)}
            onValidationChange={(isValid: boolean) => handleValidationChange(stepKey, isValid)}
            errors={formattedErrors}
          />
        );
        
      case 'review_submit':
        return (
          <ReviewSubmitStep
            companyData={onboardingData.company_info || {}}
            projectData={onboardingData.project_overview || {}}
            technicalData={onboardingData.technical_requirements as any || {}}
            timelineBudgetData={onboardingData.timeline_budget || {}}
            onEdit={(stepIndex: number) => setCurrentStep(stepIndex)}
            onSubmit={handleSubmitOnboarding}
            onValidationChange={(isValid: boolean) => handleValidationChange(stepKey, isValid)}
            isSubmitting={isSubmitting}
            submitError={submitError || undefined}
          />
        );
        
      default:
        return (
          <div>
            <Typography variant="h6">
              Step not found: {stepKey}
            </Typography>
          </div>
        );
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 2 }}>
            Get Started with VegaDigital
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
            Create your account and tell us about your project in one simple flow
          </Typography>
          
          {/* Progress indicator */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Step {currentStep + 1} of {STEPS.length}: {STEPS[currentStep].label}
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={getProgressPercentage()} 
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
        </Box>

        {/* Main content */}
        <Card elevation={2}>
          <CardContent sx={{ p: 4 }}>
            <Stepper activeStep={currentStep} orientation="vertical">
              {STEPS.map((step, index) => (
                <Step key={step.key}>
                  <StepLabel
                    optional={
                      stepValidationStates[step.key] === true ? (
                        <Chip 
                          icon={<CheckIcon />} 
                          label="Complete" 
                          size="small" 
                          color="success" 
                          variant="outlined"
                        />
                      ) : null
                    }
                    sx={{
                      cursor: index < currentStep ? 'pointer' : 'default',
                      '&:hover': index < currentStep ? { opacity: 0.7 } : {}
                    }}
                    onClick={() => index < currentStep && goToStep(index)}
                  >
                    {step.label}
                  </StepLabel>
                  <StepContent>
                    {index === currentStep && (
                      <Fade in timeout={300}>
                        <Box>
                          {/* Step content */}
                          <Box sx={{ mb: 3 }}>
                            {renderCurrentStep()}
                          </Box>

                          {/* Navigation buttons */}
                          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
                            <Button
                              onClick={handleBack}
                              disabled={currentStep === 0 || isSubmitting}
                              startIcon={<ArrowBackIcon />}
                              variant="outlined"
                            >
                              Back
                            </Button>

                            {currentStep < STEPS.length - 1 ? (
                              <Button
                                onClick={handleNext}
                                disabled={!canProceedToNext || isSubmitting}
                                endIcon={<ArrowForwardIcon />}
                                variant="contained"
                              >
                                Continue
                              </Button>
                            ) : (
                              <Button
                                onClick={handleSubmitOnboarding}
                                disabled={isSubmitting || !canProceedToNext}
                                variant="contained"
                                color="success"
                                sx={{ minWidth: 150 }}
                              >
                                {isSubmitting ? 'Creating Account...' : 'Complete Setup'}
                              </Button>
                            )}
                          </Box>
                        </Box>
                      </Fade>
                    )}
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </CardContent>
        </Card>

        {/* Success notification */}
        <Snackbar 
          open={showSuccessMessage} 
          autoHideDuration={6000} 
          onClose={() => setShowSuccessMessage(false)}
        >
          <Alert severity="success" variant="filled">
            Account created successfully! Redirecting to your dashboard...
          </Alert>
        </Snackbar>

        {/* Error notification */}
        <Snackbar 
          open={!!submitError} 
          autoHideDuration={8000} 
          onClose={() => setSubmitError(null)}
        >
          <Alert severity="error" variant="filled">
            {submitError}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default UnifiedOnboardingWizard;